// Frozen fiat currency registry — 22 supported currencies, USD default

import type { FiatCurrencyCode, FiatCurrency } from '../types/currency.ts';
import type { Option } from '../types/option.ts';
import { some, none } from '../types/option.ts';

export const DEFAULT_FIAT_CODE: FiatCurrencyCode = 'USD';

const fiat = (code: FiatCurrencyCode, name: string, symbol: string): FiatCurrency =>
  Object.freeze({ code, name, symbol });

export const FIAT_CURRENCIES: Readonly<Record<FiatCurrencyCode, FiatCurrency>> = Object.freeze({
  USD: fiat('USD', 'US Dollar', '$'),
  EUR: fiat('EUR', 'Euro', '€'),
  GBP: fiat('GBP', 'British Pound', '£'),
  CHF: fiat('CHF', 'Swiss Franc', 'Fr'),
  AUD: fiat('AUD', 'Australian Dollar', 'A$'),
  JPY: fiat('JPY', 'Japanese Yen', '¥'),
  BRL: fiat('BRL', 'Brazilian Real', 'R$'),
  INR: fiat('INR', 'Indian Rupee', '₹'),
  SGD: fiat('SGD', 'Singapore Dollar', 'S$'),
  CNY: fiat('CNY', 'Chinese Yuan', '¥'),
  RUB: fiat('RUB', 'Russian Ruble', '₽'),
  MXN: fiat('MXN', 'Mexican Peso', '$'),
  CLP: fiat('CLP', 'Chilean Peso', '$'),
  PEN: fiat('PEN', 'Peruvian Sol', 'S/'),
  AED: fiat('AED', 'UAE Dirham', 'د.إ'),
  THB: fiat('THB', 'Thai Baht', '฿'),
  IDR: fiat('IDR', 'Indonesian Rupiah', 'Rp'),
  SAR: fiat('SAR', 'Saudi Riyal', '﷼'),
  EGP: fiat('EGP', 'Egyptian Pound', 'E£'),
  KES: fiat('KES', 'Kenyan Shilling', 'KSh'),
  NGN: fiat('NGN', 'Nigerian Naira', '₦'),
  ZAR: fiat('ZAR', 'South African Rand', 'R'),
});

export const getFiatCurrency = (code: FiatCurrencyCode): Option<FiatCurrency> =>
  code in FIAT_CURRENCIES ? some(FIAT_CURRENCIES[code]) : none();

export const getAllFiatCodes = (): ReadonlyArray<FiatCurrencyCode> =>
  Object.keys(FIAT_CURRENCIES) as ReadonlyArray<FiatCurrencyCode>;

export const getDefaultFiatCurrency = (): FiatCurrency =>
  FIAT_CURRENCIES[DEFAULT_FIAT_CODE];

// Display a fiat amount the way people read money: symbol + the currency's usual decimals
// ($4.50, €4.50, ¥5). en-US keeps output stable across devices; an unknown code falls back to "4.50 XXX".
export const formatFiatAmount = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

// Same, for amounts kept as typed/decimal strings ("12.5" → "$12.50"); text that isn't a number is shown as-is
export const formatFiatText = (amount: string, currency: string): string => {
  const n = Number(amount);
  return amount.trim() !== '' && Number.isFinite(n) ? formatFiatAmount(n, currency) : `${amount} ${currency}`;
};
